<template>
  <div id="navbar-container">
    <nav class="navbar navbar-expand navbar-dark bg-dark">
      <img :src="logo" :alt="title" class="navbar-logo mr-2">
      <router-link to="/" class="navbar-brand site-title" :style="titleStyle">{{ title }}</router-link>
      
      <!-- 主要导航项，这些始终显示 -->
      <div class="main-nav-items">
        <router-link to="/aimode" class="main-nav-link" style="font-weight:600; color:white;">AI陪练</router-link>
        <a href="#" class="main-nav-link" style="font-weight:600; color:white;" @click.prevent="isLoggedIn ? $router.push('/pre-match') : showAuthModal = true">🔥匹配模式</a>
      </div>
      
      <!-- 移动端菜单按钮，只折叠次要导航项 -->
      <button class="navbar-toggler mobile-only" type="button" @click="toggleMobileMenu">
        <span class="navbar-toggler-icon"></span>
      </button>
      
      <!-- 桌面端次要导航项 -->
      <div class="navbar-nav mr-auto ml-4 desktop-menu secondary-nav">
        <li class="nav-item">
          <a href="#" class="nav-link" style="font-weight:600; color:white; font-size:1.25rem;" @click.prevent="handleInviteClick()">邀请有礼</a>
        </li>
        <!-- VIP按钮已被注释掉，后续可取消注释 -->
        <!--
        <li class="nav-item">
          <a href="#" class="nav-link vip-link" style="color:gray; font-size:1.25rem;" @click.prevent="isLoggedIn ? showVipModal = true : showAuthModal = true">
            <i class="fas fa-crown"></i> 开通VIP
          </a>
        </li>
        -->
      </div>
      <div class="navbar-nav ml-auto desktop-menu">
        <!-- Theme selector buttons -->
        <li class="nav-item theme-selector">
          <button class="theme-btn light" @click="changeTheme('light')" :class="{ active: currentTheme === 'light' }">
            <i class="fas fa-sun"></i>
          </button>
          <button class="theme-btn dark" @click="changeTheme('dark')" :class="{ active: currentTheme === 'dark' }">
            <i class="fas fa-moon"></i>
          </button>
        </li>
        <li class="nav-item">
          <a href="#" class="nav-link" @click.prevent="showChatGroup">交流群</a>
        </li>
        <li class="nav-item">
          <a href="#" class="nav-link" @click.prevent="showFeedback">建议反馈</a>
        </li>
        <li class="nav-item" v-if="!isLoggedIn">
          <a href="#" class="nav-link" @click.prevent="showAuthModal = true">登录</a>
        </li>
        <li class="nav-item" v-if="isLoggedIn">
          <a href="#" class="nav-link" @click.prevent="logOut">登出</a>
        </li>
      </div>
    </nav>
    
    <!-- 移动端折叠菜单，只包含次要导航项 -->
    <div class="mobile-menu" v-if="showMobileMenu">
      <ul class="mobile-menu-list">
        <li class="mobile-menu-item">
          <a href="#" class="mobile-menu-link" @click.prevent="handleMobileMenuClick('invite')">邀请有礼</a>
        </li>
        <!-- 移动端VIP按钮已被注释掉，后续可取消注释 -->
        <!--
        <li class="mobile-menu-item">
          <a href="#" class="mobile-menu-link vip-link" @click.prevent="handleMobileMenuClick('vip')">
            <i class="fas fa-crown"></i> 开通VIP
          </a>
        </li>
        -->
        <li class="mobile-menu-item">
          <a href="#" class="mobile-menu-link" @click.prevent="showChatGroup(); closeMobileMenu();">交流群</a>
        </li>
        <li class="mobile-menu-item">
          <a href="#" class="mobile-menu-link" @click.prevent="showFeedback(); closeMobileMenu();">建议反馈</a>
        </li>
        <li class="mobile-menu-item" v-if="!isLoggedIn">
          <a href="#" class="mobile-menu-link" @click.prevent="showAuthModal = true; closeMobileMenu();">登录</a>
        </li>
        <li class="mobile-menu-item" v-if="isLoggedIn">
          <a href="#" class="mobile-menu-link" @click.prevent="logOut(); closeMobileMenu();">登出</a>
        </li>
        <li class="mobile-menu-item theme-toggle">
          <span>主题:</span>
          <button class="theme-btn-mobile light" @click="changeTheme('light')" :class="{ active: currentTheme === 'light' }">
            <i class="fas fa-sun"></i>
          </button>
          <button class="theme-btn-mobile dark" @click="changeTheme('dark')" :class="{ active: currentTheme === 'dark' }">
            <i class="fas fa-moon"></i>
          </button>
        </li>
      </ul>
    </div>
    
    <!-- 这里添加其他模态框，例如Auth Modal, VIP Modal等 -->
    <div v-if="useBuiltInModals">
      <auth-modal 
        :show="showAuthModal" 
        @close="handleModalClose"
        @login-success="onLoginSuccess"
        @login-failed="resetInteraction"
      />
      
      <!-- VIP会员模态框 -->
      <vip-modal
        :show="showVipModal"
        @close="showVipModal = false"
      />
    </div>
    
    <!-- 邀请码模态框 -->
    <div class="invitation-modal" v-if="showInviteModal">
      <div class="invitation-overlay" @click="showInviteModal = false"></div>
      <div class="invitation-container">
        <div class="invitation-header">
          <h3>邀请有礼</h3>
          <button class="invitation-close" @click="showInviteModal = false">×</button>
        </div>
        
        <div class="invitation-body">
          <p class="invitation-description">邀请好友加入即答，让大家一起备战面试！</p>
          
          <div v-if="inviteLoading" class="invitation-loading">
            <div class="spinner"></div>
            <span>获取邀请码中...</span>
          </div>
          
          <div v-else-if="inviteError" class="invitation-error">
            <p>{{ inviteError }}</p>
            <button class="btn-retry" @click="generateInviteCode">重试</button>
          </div>
          
          <div v-else-if="inviteCode" class="invitation-content">
            <div class="invite-code-wrapper">
              <h4>您的专属邀请码</h4>
              <div class="invite-code-display">{{ inviteCode }}</div>
              <div class="invite-actions">
                <button class="btn-copy" @click="copyInviteCode">
                  <i class="fas fa-copy"></i> 复制
                </button>
              </div>
            </div>
            
            <div class="invitation-info">
              <div class="info-item">
                <span class="info-label">已邀请人数:</span>
                <span class="info-value">{{ totalInvites || 0 }}</span>
              </div>
              <div class="info-item" v-if="inviteExpires">
                <span class="info-label">有效期至:</span>
                <span class="info-value">{{ formatDate(inviteExpires) }}</span>
              </div>
            </div>
            
            <div class="invitation-tips">
              <h4>邀请方式</h4>
              <ol>
                <li>点击"复制"按钮复制您的邀请码</li>
                <li>将邀请码分享给好友</li>
                <li>好友注册时填写您的邀请码即可</li>
              </ol>
            </div>
          </div>
          
          <div v-else class="invitation-content">
            <p>您还没有邀请码，点击下方按钮生成一个邀请码。</p>
            <button class="btn-generate" @click="generateInviteCode">生成邀请码</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 可选导入需要的组件
// import AuthModal from '../AuthModal.vue';
// import VipModal from '../VipModal.vue';
import InvitationService from '@/services/invitation.service';

export default {
  name: 'NavBar',
  components: {
    // AuthModal,
    // VipModal
  },
  props: {
    logo: {
      type: String,
      default: '/jida.png'
    },
    title: {
      type: String,
      default: '即答公考'
    },
    titleStyle: {
      type: Object,
      default: () => ({ color: 'gray' })
    },
    currentTheme: {
      type: String,
      default: 'light'
    },
    isLoggedIn: {
      type: Boolean,
      default: false
    },
    useBuiltInModals: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showAuthModal: false,
      showInviteModal: false,
      showVipModal: false,
      showMobileMenu: false,
      // 邀请码相关
      inviteCode: '',
      inviteExpires: null,
      maxUses: 0,
      currentUses: 0,
      totalInvites: 0,
      inviteLoading: false,
      inviteError: null,
      hasInteracted: false,
      authCheckCompleted: false
    };
  },
  watch: {
    showInviteModal(val) {
      if (val && this.isLoggedIn) {
        this.checkInviteCode();
      }
    }
  },
  mounted() {
    // 应用挂载时的操作
    if (this.currentTheme) {
      document.body.setAttribute('data-theme', this.currentTheme);
    }
    
    // 添加窗口大小变化监听器
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    // 移除监听器
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    // 页面交互方法
    toggleMobileMenu() {
      this.showMobileMenu = !this.showMobileMenu;
      this.$emit('mobile-menu-toggle', this.showMobileMenu);
    },
    closeMobileMenu() {
      if (this.showMobileMenu) {
        this.showMobileMenu = false;
        this.$emit('mobile-menu-toggle', false);
      }
    },
    handleResize() {
      // 当窗口宽度大于移动断点时关闭移动菜单
      if (window.innerWidth > 768 && this.showMobileMenu) {
        this.closeMobileMenu();
      }
    },
    changeTheme(theme) {
      // 触发自定义事件通知父组件主题已更改
      this.$emit('theme-change', theme);
      
      // 直接更新document.body的属性以实时响应主题变化
      document.body.setAttribute('data-theme', theme);
    },
    
    // 用户操作处理
    logOut() {
      this.$emit('logout');
    },
    showFeedback() {
      this.$emit('show-feedback');
    },
    showChatGroup() {
      alert('添加微信 jidagk 进入面试交流群');
    },
    handleMobileMenuClick(item) {
      // 触发移动菜单操作事件
      this.$emit('mobile-menu-action', item);
      this.closeMobileMenu();
      
      // 直接处理邀请功能的点击
      if (item === 'invite') {
        this.handleInviteClick();
      }
    },
    
    // 处理邀请点击
    handleInviteClick() {
      if (this.isLoggedIn) {
        this.showInviteModal = true;
      } else {
        this.showAuthModal = true;
      }
    },
    
    // 模态框相关
    handleModalClose() {
      this.showAuthModal = false;
      this.$emit('auth-modal-close');
    },
    onLoginSuccess() {
      this.$emit('login-success');
    },
    resetInteraction() {
      this.hasInteracted = false;
      this.$emit('reset-interaction');
    },
    
    // 邀请码相关方法
    async checkInviteCode() {
      if (!this.isLoggedIn) return;
      
      this.inviteLoading = true;
      this.inviteError = null;
      
      try {
        console.log('开始检查邀请码...');
        const response = await InvitationService.checkInviteCode();
        console.log('邀请码检查响应:', response);
        
        if (response.success) {
          if (response.hasCode) {
            this.inviteCode = response.code;
            this.maxUses = response.maxUses;
            this.currentUses = response.currentUses;
            this.inviteExpires = response.expiresAt ? new Date(response.expiresAt) : null;
          } else {
            this.inviteCode = '';
          }
          this.totalInvites = response.totalInvites || 0;
        } else {
          this.inviteError = response.message || '获取邀请码信息失败';
        }
      } catch (error) {
        console.error('检查邀请码出错:', error);
        this.inviteError = '获取邀请码信息失败，请稍后再试';
      } finally {
        this.inviteLoading = false;
      }
    },
    
    // 生成邀请码
    async generateInviteCode() {
      if (!this.isLoggedIn) return;
      
      this.inviteLoading = true;
      this.inviteError = null;
      
      try {
        console.log('开始生成邀请码...');
        const response = await InvitationService.generateInviteCode();
        console.log('生成邀请码响应:', response);
        
        if (response.success) {
          this.inviteCode = response.code;
          // 重新获取完整的邀请码信息
          await this.checkInviteCode();
        } else {
          this.inviteError = response.message || '生成邀请码失败';
        }
      } catch (error) {
        console.error('生成邀请码出错:', error);
        this.inviteError = '生成邀请码失败，请稍后再试';
      } finally {
        this.inviteLoading = false;
      }
    },
    
    // 复制邀请码到剪贴板
    copyInviteCode() {
      if (!this.inviteCode) return;
      
      // 创建临时文本区域
      const textArea = document.createElement('textarea');
      textArea.value = this.inviteCode;
      document.body.appendChild(textArea);
      
      // 选择文本并复制
      textArea.select();
      document.execCommand('copy');
      
      // 移除临时元素
      document.body.removeChild(textArea);
      
      // 提示用户已复制
      alert('邀请码已复制到剪贴板');
    },
    
    // 格式化日期
    formatDate(date) {
      if (!date) return '';
      
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
  }
};
</script>

<style>
/* 全局样式 */
* {
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

/* 移动端优化 */
@media (max-width: 768px) {
  html {
    font-size: 14px; /* 移动端基础字体稍小 */
  }
  
  input, button, select, textarea {
    font-size: 16px !important; /* 防止iOS自动缩放 */
  }
  
  /* 改善移动端点击响应区域 */
  button, 
  [role="button"], 
  input[type="button"], 
  input[type="submit"], 
  input[type="reset"] {
    min-height: 44px;
    min-width: 44px;
  }
  
  /* 增强移动端滚动体验 */
  .scrollable-container {
    -webkit-overflow-scrolling: touch;
  }
  
  .mobile-nav-container {
    display: flex;
  }
  
  .desktop-only {
    display: none !important;
  }
  
  .mobile-only {
    display: block !important;
  }
  
  /* 适配底部导航空间 */
  .has-bottom-nav {
    padding-bottom: 60px;
  }
  
  /* 导航栏移动端适配 - 保留但修改部分属性 */
  .nav-item {
    margin: 0 0.25rem;
  }
  
  .nav-link {
    padding: 0.375rem 0.5rem;
    font-size: 0.95rem !important;
    white-space: nowrap;
  }
  
  .theme-selector {
    margin-right: 5px;
  }
  
  .theme-btn {
    padding: 3px 6px;
  }
}

@media (max-width: 480px) {
  .navbar-nav .nav-link {
    font-size: 0.85rem !important;
    padding: 0.25rem 0.4rem;
  }
  
  .navbar-nav .nav-item {
    margin: 0 0.125rem;
  }
  
  /* 移除影响标题和主导航的样式 */
  /* .navbar-brand {
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  } */
  
  /* 更紧凑的布局 - 由新的样式处理 */
  /* .navbar {
    padding: 0.375rem;
  } */
  
  .mobile-menu-link {
    padding: 12px 16px;
    font-size: 0.95rem;
  }
  
  .theme-toggle {
    padding: 12px 16px;
  }
  
  .theme-btn-mobile {
    padding: 6px 10px;
  }
  
  .mobile-menu {
    top: 48px; /* 调整为更小的导航栏高度 */
    max-height: calc(100vh - 48px);
  }
  
  /* 优化小屏幕下其他容器的边距 */
  .container {
    padding-left: 10px;
    padding-right: 10px;
  }
  
  .icp-footer {
    padding: 10px 0;
    font-size: 12px;
  }
}

@media (max-width: 360px) {
  /* .navbar-brand {
    max-width: 60px;
  } */
  
  .mobile-menu-link {
    padding: 10px 14px;
    font-size: 0.9rem;
  }
  
  /* 极小屏幕上使图标更小 */
  .navbar-toggler-icon {
    width: 1.25em;
    height: 1.25em;
  }
  
  /* 调整最小屏幕下的容器边距 */
  .container {
    padding-left: 8px;
    padding-right: 8px;
  }
}

@media (max-height: 500px) and (orientation: landscape) {
  /* 处理横屏且高度较小的情况 */
  .mobile-menu {
    max-height: 85vh;
    overflow-y: auto;
  }
  
  .mobile-menu-list {
    display: flex;
    flex-wrap: wrap;
  }
  
  .mobile-menu-item {
    width: 50%;
    border-bottom: none;
    border-right: 1px solid rgba(255, 255, 255, 0.1);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .mobile-menu-link {
    padding: 10px 15px;
    display: flex;
    align-items: center;
    height: 100%;
  }
}

/* 适配不同设备像素比 */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  /* 优化高分辨率显示屏上的1px边框 */
  .hairline-border {
    position: relative;
  }
  .hairline-border::after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 200%;
    height: 200%;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: inherit;
    transform: scale(0.5);
    transform-origin: 0 0;
    pointer-events: none;
  }
}

/* 移动端导航适配 */
.mobile-nav-container {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background: #fff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: none;
}

/* 页面整体样式 */
#navbar-container {
  position: relative;
}

.navbar-brand {
  font-weight: 600;
}

.navbar {
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

/* Logo in the navbar */
.navbar-logo {
  height: 30px;
  width: auto;
  margin-right: 10px;
}

/* Theme selector styles */
.theme-selector {
  display: flex;
  align-items: center;
  margin-right: 15px;
}

.theme-btn {
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 5px 10px;
  margin: 0 2px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.theme-btn.active {
  background: rgba(255, 255, 255, 0.2);
}

.theme-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* 网站标题样式 */
.site-title {
  font-weight: 600;
  white-space: nowrap;
  overflow: visible;
  display: block;
  max-width: none;
}

/* 主导航项样式 - 这些总是可见 */
.main-nav-items {
  display: flex;
  align-items: center;
  margin-left: 1rem;
}

.main-nav-link {
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  margin: 0 0.25rem;
  text-decoration: none;
  font-size: 1.25rem;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.main-nav-link:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  text-decoration: none;
}

/* 移动端菜单样式 */
.mobile-only {
  display: none;
}

.navbar-toggler {
  border: none;
  background: transparent;
  padding: 5px 8px;
  margin-left: auto;
  cursor: pointer;
}

.navbar-toggler-icon {
  display: inline-block;
  width: 1.5em;
  height: 1.5em;
  vertical-align: middle;
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg viewBox='0 0 30 30' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke='rgba(255, 255, 255, 0.8)' stroke-width='2' stroke-linecap='round' stroke-miterlimit='10' d='M4 7h22M4 15h22M4 23h22'/%3E%3C/svg%3E");
  background-size: 100% 100%;
}

.mobile-menu {
  position: fixed;
  top: 56px; /* 导航栏高度 */
  left: 0;
  right: 0;
  background: #343a40;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideDown 0.3s ease;
  overflow-y: auto;
  max-height: calc(100vh - 56px);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mobile-menu-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.mobile-menu-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.mobile-menu-item:last-child {
  border-bottom: none;
}

.mobile-menu-link {
  display: block;
  padding: 15px 20px;
  color: white;
  font-weight: 500;
  text-decoration: none;
  transition: background-color 0.2s;
}

.mobile-menu-link:hover, 
.mobile-menu-link:active {
  background-color: rgba(255, 255, 255, 0.1);
  text-decoration: none;
  color: white;
}

.theme-toggle {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  color: white;
}

.theme-toggle span {
  margin-right: 15px;
}

.theme-btn-mobile {
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 8px 12px;
  margin: 0 5px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.theme-btn-mobile.active {
  background: rgba(255, 255, 255, 0.2);
}

.mobile-menu-link.vip-link {
  color: #f8d62b;
}

@media (max-width: 768px) {
  .desktop-menu {
    display: none !important;
  }
  
  .mobile-only {
    display: block !important;
  }
  
  .site-title {
    font-size: 0.95rem;
  }
  
  .main-nav-items {
    margin-left: 0.5rem;
  }
  
  .main-nav-link {
    font-size: 0.95rem;
    padding: 0.375rem 0.5rem;
    margin: 0 0.125rem;
  }
  
  .navbar-toggler {
    padding: 0.25rem 0.5rem;
    margin-left: 0.25rem;
  }
  
  /* 确保移动端显示布局不变 */
  .navbar {
    justify-content: flex-start;
    flex-wrap: nowrap;
    padding: 0.5rem 0.75rem;
  }
  
  .navbar-logo {
    margin-right: 0.5rem;
  }
  
  .secondary-nav {
    display: none !important;
  }
}

@media (max-width: 480px) {
  .site-title {
    font-size: 0.85rem;
    max-width: none;
  }
  
  .main-nav-link {
    font-size: 0.85rem;
    padding: 0.3rem 0.4rem;
    margin: 0 0.1rem;
  }
  
  .navbar-logo {
    height: 20px;
    margin-right: 0.25rem;
  }
  
  .navbar {
    padding: 0.4rem 0.5rem;
  }
}

@media (max-width: 380px) {
  .site-title {
    font-size: 0.8rem;
  }
  
  .main-nav-link {
    font-size: 0.8rem;
    padding: 0.25rem 0.35rem;
  }
  
  .navbar-logo {
    height: 18px;
  }
}

/* 深色模式支持 */
[data-theme="dark"] .navbar {
  background-color: #1a1a1a !important;
}

[data-theme="dark"] .mobile-menu {
  background-color: #1a1a1a;
}

/* Keep the navbar dark in both themes */
.navbar-dark {
  background-color: #343a40 !important;
}

/* 邀请码模态框样式 */
.invitation-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9800; /* 低于登录弹窗(10000)和VIP弹窗(9900) */
}

.invitation-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
}

.invitation-container {
  position: relative;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 420px;
  z-index: 1;
  overflow: hidden;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.invitation-header {
  padding: 16px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.invitation-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.invitation-close {
  background: transparent;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.invitation-close:hover {
  opacity: 1;
}

.invitation-body {
  padding: 20px;
}

.invitation-description {
  text-align: center;
  color: #4b5563;
  margin-bottom: 20px;
}

.invitation-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 0;
}

.invitation-loading .spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #6366f1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.invitation-error {
  text-align: center;
  padding: 20px 0;
}

.invitation-error p {
  color: #ef4444;
  margin-bottom: 15px;
}

.btn-retry {
  background: #f3f4f6;
  color: #4b5563;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-retry:hover {
  background: #e5e7eb;
}

.invitation-content {
  padding: 10px 0;
}

.invite-code-wrapper {
  background: #f9fafb;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  text-align: center;
}

.invite-code-wrapper h4 {
  margin: 0 0 10px 0;
  color: #4b5563;
  font-size: 16px;
}

.invite-code-display {
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 1px;
  color: #6366f1;
  background: white;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 15px;
  user-select: all;
}

.invite-actions {
  display: flex;
  justify-content: center;
}

.btn-copy, .btn-generate {
  background: #6366f1;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-copy:hover, .btn-generate:hover {
  background: #4f46e5;
}

.invitation-info {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
}

.info-item {
  flex: 1;
  min-width: 120px;
  background: #f3f4f6;
  padding: 10px;
  border-radius: 6px;
  text-align: center;
}

.info-label {
  display: block;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 5px;
}

.info-value {
  font-weight: 600;
  color: #4b5563;
  font-size: 16px;
}

.invitation-tips {
  background: #f9fafb;
  border-radius: 8px;
  padding: 15px;
}

.invitation-tips h4 {
  margin: 0 0 10px 0;
  color: #4b5563;
  font-size: 16px;
}

.invitation-tips ol {
  margin: 0;
  padding-left: 20px;
}

.invitation-tips li {
  color: #6b7280;
  margin-bottom: 5px;
}

@media (max-width: 480px) {
  .invitation-container {
    max-width: 95%;
  }
  
  .invitation-body {
    padding: 15px;
  }
  
  .invite-code-display {
    font-size: 20px;
    letter-spacing: 0.5px;
  }
}

/* VIP link styling */
.vip-link {
  color: #f8d62b !important;
  font-weight: bold;
  display: flex;
  align-items: center;
}

.vip-link i {
  margin-right: 0.25rem;
}

.vip-link:hover {
  color: #ffea8a !important;
}
</style> 